import React, { ReactNode, useRef } from 'react';

import { Accordion, AccordionDetails, AccordionProps, AccordionSummary } from '../Accordion';
import { Box } from '../Box';
import { ArrowDropDownIcon } from '../Icons';
import { FormGroup, useMediaQuery } from '@mui/material';

export interface ChipSelectProps extends Required<Pick<AccordionProps, 'expanded' | 'onChange'>> {
    'data-testid': string;
    expandIconId?: string;
    disabled?: boolean;
    label: ReactNode;
    children: ReactNode | ReactNode[];
    active?: boolean;
}
/**
 * An accordion that looks like a chip for the purpose of holding form elements that
 * the user can interact with. For example, this chip can be given a list of checkboxes
 * that the user can click.
 */
const ChipSelect = ({
    children,
    'data-testid': dataTestId,
    expanded,
    expandIconId,
    label,
    disabled = false,
    onChange,
    active
}: ChipSelectProps) => {
    const summaryRef = useRef<HTMLDivElement>(null);
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

    // maxWidth that doesn't look to odd on load
    const maxChipWidth = 205;

    return (
        <Accordion
            data-testid={dataTestId}
            elevation={0}
            expanded={expanded}
            onChange={onChange}
            sx={(theme) => ({
                margin: 0,
                zIndex: 11,
                width: 'fit-content',
                borderColor: prefersDarkMode
                    ? theme.palette.common.white
                    : active
                      ? theme.palette.common.black
                      : theme.palette.grey[500],
                borderRadius: theme.mixins.chip?.borderRadius,
                borderStyle: 'solid',
                borderWidth: active ? (prefersDarkMode ? '3px' : '2.5px') : '1px',
                '&:last-of-type': { borderRadius: theme.mixins.chip?.borderRadius },
                '&.Mui-expanded': {
                    margin: 0,
                    overflowY: 'hidden',
                    maxHeight: '85vh'
                },
                '&.Mui-disabled': {
                    backgroundColor: theme.palette.disabledGray.dark
                }
            })}
        >
            <AccordionSummary
                ref={summaryRef}
                sx={(theme) => ({
                    minHeight: theme.mixins.chip?.height,
                    height: theme.mixins.chip?.height,
                    padding: '0 6px',
                    alignContents: 'center',
                    backgroundColor: prefersDarkMode
                        ? theme.palette.secondary.main
                        : active
                          ? theme.palette.secondary.main
                          : theme.palette.grey[200],
                    borderWidth: '0',
                    borderRadius: theme.mixins.chip?.borderRadius,
                    color: theme.palette.secondary.contrastText,
                    display: 'in-line',
                    justifyContent: 'right',
                    ...(disabled && {
                        backgroundColor: theme.palette.disabledGray.dark,
                        opacity: 0.5
                    }),
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        color: theme.palette.getContrastText(theme.palette.action.hover)
                    },
                    '&.Mui-expanded': {
                        minHeight: 0
                    },
                    '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': {
                        margin: '0px 0'
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                        color: 'inherit'
                    },
                    '&.Mui-disabled': {
                        cursor: 'pointer'
                    }
                })}
                expandIcon={<ArrowDropDownIcon id={expandIconId} />}
            >
                {label}
            </AccordionSummary>
            <AccordionDetails
                sx={{
                    padding: 0,
                    width: '100%',
                    position: 'relative',
                    maxHeight: '60vh',
                    overflowY: 'auto'
                }}
                data-testid={`${dataTestId}-details`}
            >
                <Box sx={{ position: 'relative', paddingBottom: 1, paddingTop: 1 }}>
                    {/* FormGroup aligns the elements vertically */}
                    <FormGroup sx={{ marginX: '12px', width: `${maxChipWidth}px` }}>
                        {children && [...(Array.isArray(children) ? children : [children])]}
                    </FormGroup>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default ChipSelect;
